/**
 * Spanish render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'es')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const spanishLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'desenfocar' },
    change: { primary: 'cambiar' },
    click: { primary: 'clic' },
    dblclick: { primary: 'dobleclic' },
    focus: { primary: 'enfocar' },
    input: { primary: 'entrada' },
    keydown: { primary: 'tecla abajo' },
    keypress: { primary: 'teclapresar' },
    keyup: { primary: 'tecla arriba' },
    load: { primary: 'cargar' },
    mousedown: { primary: 'ratón abajo' },
    mouseenter: { primary: 'ratónentrar' },
    mouseleave: { primary: 'ratónsalir' },
    mousemove: { primary: 'ratónmover' },
    mouseout: { primary: 'ratón fuera' },
    mouseover: { primary: 'ratón encima' },
    mouseup: { primary: 'ratón arriba' },
    reset: { primary: 'reiniciar' },
    resize: { primary: 'redimensionar' },
    scroll: { primary: 'desplazar' },
    submit: { primary: 'envío' },
    touchcancel: { primary: 'toquecancelar' },
    touchend: { primary: 'toqueterminar' },
    touchmove: { primary: 'toquemover' },
    touchstart: { primary: 'toqueempezar' },
    unload: { primary: 'descargar' },
  },
  logical: {
    and: { primary: 'y' },
    changes: { primary: 'cambia' },
    contains: { primary: 'contiene' },
    else: { primary: 'sino' },
    end: { primary: 'fin' },
    equals: { primary: 'iguala' },
    exists: { primary: 'existe' },
    has: { primary: 'tiene' },
    have: { primary: 'tengo' },
    includes: { primary: 'incluye' },
    is: { primary: 'es' },
    live: { primary: 'vivo' },
    matches: { primary: 'coincide' },
    not: { primary: 'no' },
    or: { primary: 'o' },
    otherwise: { primary: 'delocontrario' },
    then: { primary: 'entonces' },
    when: { primary: 'cuando' },
    where: { primary: 'donde' },
  },
  temporal: {
    forever: { primary: 'siempre' },
    h: { primary: 'h' },
    hour: { primary: 'hora' },
    hours: { primary: 'horas' },
    millisecond: { primary: 'milisegundo' },
    milliseconds: { primary: 'milisegundos' },
    min: { primary: 'min' },
    minute: { primary: 'minuto' },
    minutes: { primary: 'minutos' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'segundo' },
    seconds: { primary: 'segundos' },
    times: { primary: 'veces' },
  },
  values: {
    body: { primary: 'cuerpo' },
    detail: { primary: 'detalle' },
    document: { primary: 'documento' },
    element: { primary: 'elemento' },
    event: { primary: 'evento' },
    false: { primary: 'falso' },
    it: { primary: 'ello' },
    its: { primary: 'su' },
    me: { primary: 'yo' },
    my: { primary: 'mi' },
    myself: { primary: 'yo mismo' },
    null: { primary: 'nulo' },
    result: { primary: 'resultado' },
    target: { primary: 'objetivo' },
    true: { primary: 'verdadero' },
    undefined: { primary: 'indefinido' },
    value: { primary: 'valor' },
    window: { primary: 'ventana' },
    you: { primary: 'tu' },
    your: { primary: 'tu' },
    yourself: { primary: 'ti mismo' },
  },
  attributes: {
    attribute: { primary: 'atributo' },
    attributes: { primary: 'atributos' },
    class: { primary: 'clase' },
    classes: { primary: 'clases' },
    properties: { primary: 'propiedades' },
    property: { primary: 'propiedad' },
    style: { primary: 'estilo' },
    styles: { primary: 'estilos' },
  },
  expressions: {
    up: { primary: 'arriba' },
    down: { primary: 'abajo' },
    left: { primary: 'izquierda' },
    right: { primary: 'derecha' },
    at: { primary: 'en' },
    back: { primary: 'atrás' },
    characters: { primary: 'caracteres' },
    children: { primary: 'hijos' },
    closest: { primary: 'cercano' },
    elsewhere: { primary: 'afuera' },
    empty: { primary: 'vacío' },
    'ends with': { primary: 'termina con' },
    exclusive: { primary: 'exclusivo' },
    first: { primary: 'primero' },
    'ignoring case': { primary: 'ignorando mayúsculas' },
    inclusive: { primary: 'inclusivo' },
    index: { primary: 'índice' },
    'joined by': { primary: 'unido por' },
    last: { primary: 'último' },
    'mapped to': { primary: 'transformado a' },
    next: { primary: 'siguiente' },
    no: { primary: 'ningún' },
    parent: { primary: 'padre' },
    prev: { primary: 'ant' },
    previous: { primary: 'anterior' },
    random: { primary: 'aleatorio' },
    some: { primary: 'algún' },
    'sorted by': { primary: 'ordenado por' },
    'split by': { primary: 'dividido por' },
    start: { primary: 'comenzar' },
    catch: { primary: 'atrapar' },
    finally: { primary: 'finalmente' },
    def: { primary: 'función' },
    'starts with': { primary: 'empieza con' },
    within: { primary: 'dentro' },
  },
};

registerLexicon('es', spanishLexicon);
